import { DivergingBarCell, StackedBarCell, cn } from "@fox1ck-org/ui";

import { DASH, formatChange, formatInt, formatMoney, formatPct, formatPp } from "@/lib/format";
import type { Bag, Format } from "@/lib/campaigns-table";
import { useT } from "@/lib/i18n";

/**
 * One number of the campaigns table, through the app's one formatter.
 * Rates are muted beside the counts they are made of; profit and ROI carry
 * their sign in colour (доска Main).
 */
export function formatValue(format: Format, v: number | undefined): string {
  switch (format) {
    case "int":
      return formatInt(v);
    case "pct1":
      return formatPct(v);
    case "pct0":
      return formatPct(v, { digits: 0 });
    case "money":
      return formatMoney(v);
    case "unit":
      return formatMoney(v, { unit: true });
    case "profit":
      return formatMoney(v, { signed: true });
    case "roi":
      return formatPct(v, { digits: 0, signed: true });
    case "outcome":
      return DASH;
  }
}

export function ValueCell({
  format,
  value,
  dim,
  strong,
}: {
  format: Format;
  value: number | undefined;
  dim?: boolean;
  strong?: boolean;
}) {
  const signed = format === "roi" || format === "profit";
  return (
    <span
      className={cn(
        "font-mono text-sm tabular-nums",
        dim && "text-muted-foreground",
        strong && "font-semibold",
        signed && value !== undefined && value > 0 && "text-ok",
        signed && value !== undefined && value < 0 && "text-destructive",
      )}
    >
      {formatValue(format, value)}
    </span>
  );
}

/** Profit as a bar around zero plus the number (доска Main, «Профит»). */
export function ProfitCell({ value, max, strong }: { value: number | undefined; max: number; strong?: boolean }) {
  return (
    <DivergingBarCell
      value={value}
      max={max}
      placeholder={DASH}
      display={<ValueCell format="profit" value={value} strong={strong} />}
    />
  );
}

/** What became of the leads: апрув · холд · отказ · треш, as shares of one bar. */
export function OutcomeCell({ v }: { v: Bag }) {
  const t = useT();
  return (
    <StackedBarCell
      placeholder={DASH}
      segments={[
        { value: v.conversions ?? 0, tone: "ok", label: t("cols.approved") },
        { value: v.conversions_hold ?? 0, tone: "neutral", label: t("cols.hold") },
        { value: v.rejected ?? 0, tone: "danger", label: t("cols.rejected") },
        { value: v.trash ?? 0, tone: "warn", label: t("cols.trash") },
      ]}
    />
  );
}

/**
 * The change against the comparison window: relative for counts and money,
 * percentage points for rates, absolute for profit (доска Main: «+951»).
 */
export function deltaText(format: Format, cur: number | undefined, prev: number | undefined): string {
  if (cur === undefined || prev === undefined) return DASH;
  if (format === "pct0" || format === "pct1" || format === "roi") return formatPp(cur - prev);
  if (format === "profit") return formatMoney(cur - prev, { signed: true });
  return formatChange(cur, prev);
}

/** Up is good, except for what costs money. */
export function deltaTone(key: string, cur: number | undefined, prev: number | undefined): "pos" | "neg" | undefined {
  if (cur === undefined || prev === undefined || cur === prev) return undefined;
  const up = cur > prev;
  const costly = key === "cost" || key === "cpa" || key === "cpc" || key === "cpl" || key === "bots" || key === "bots_pct";
  return up !== costly ? "pos" : "neg";
}

export function DeltaCell({
  format,
  k,
  cur,
  prev,
}: {
  format: Format;
  k: string;
  cur: number | undefined;
  prev: number | undefined;
}) {
  const tone = deltaTone(k, cur, prev);
  return (
    <span
      className={cn(
        "font-mono text-xs tabular-nums",
        tone === "pos" ? "text-ok" : tone === "neg" ? "text-destructive" : "text-muted-foreground",
      )}
    >
      {deltaText(format, cur, prev)}
    </span>
  );
}

